import React, { useMemo } from 'react'
import {
  Box,
  Chip,
  Select,
  MenuItem,
  FormLabel,
  FormControl,
  LinearProgress,
  FormHelperText,
} from '@mui/material'
import { ExpandMoreIcon } from '@/icons'
import { CommonLoader, CommonCheckbox } from './index'
import {
  isArray,
  isObject,
  getLabel,
  sortOptions,
  getOptionsMap,
  getSelectLabel,
  isFunctionText,
  getSelectValue,
  getComputedWidth,
  getValueValidity,
  getSelectChangePayload,
} from './utils'

export const CommonSelect = ({
  id,
  label = '',
  value,
  error,
  width,
  loading,
  helperText,
  returnObject,
  options = [],
  customText = '',
  placeholder = '',
  showChips = true,
  fullWidth = true,
  readOnly = false,
  required = false,
  enableSort = true,
  optionText = 'name',
  optionValue = 'id',
  showCheckboxes = false,
  setValue,
  onChange,
  children,
  ...muiProps
}) => {
  const { multiple: multipleProp, groupBy: groupByProp } = muiProps

  const handleChange = newValue => {
    if (setValue) setValue(newValue)
    if (onChange) onChange(newValue)
  }

  const isValueArray = useMemo(() => {
    return isArray(value)
  }, [value])

  const isValueValid = useMemo(() => {
    return getValueValidity(value, options, optionValue)
  }, [value, options])

  const optionsMap = useMemo(() => {
    return getOptionsMap(options, optionValue)
  }, [options, optionValue])

  const computedOptions = useMemo(() => {
    if (!enableSort) return options
    return sortOptions(options, optionText, groupByProp)
  }, [value, options])

  const selectValue = useMemo(() => {
    return getSelectValue(value, optionValue, isValueValid, isValueArray)
  }, [value, isValueValid])

  const getSelectRenderValue = selected => {
    if (customText) return customText

    const label = getSelectLabel(selected, options, optionText, optionValue)

    if (!label && placeholder) {
      return (
        <span
          style={{
            fontSize: '16px',
            color: '#a3a3a3',
            WebkitTextFillColor: '#a3a3a3',
          }}
        >
          {placeholder}
        </span>
      )
    }

    if (showChips && multipleProp) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map(value => (
            <Chip
              key={value}
              size='small'
              variant='outlined'
              color='primary'
              sx={{ borderRadius: '12px' }}
              label={getSelectLabel(value, options, optionText, optionValue)}
            />
          ))}
        </Box>
      )
    }

    return label
  }

  const isOptionChecked = option => {
    if (!isValueArray) return false

    const isOptionObject = isObject(option)

    if (isOptionObject && !returnObject)
      return value.includes(option[optionValue])

    return value.includes(option)
  }

  const handleSelectChange = newValue => {
    const payload = getSelectChangePayload(
      newValue,
      isValueArray,
      optionsMap,
      returnObject
    )
    handleChange(payload)
  }

  return (
    <Box data-testid={id} sx={{ width: getComputedWidth(width, fullWidth) }}>
      <FormControl
        fullWidth
        error={!!error}
        className={loading || !computedOptions.length ? 'inert' : ''}
      >
        <FormLabel>{getLabel(label, required)}</FormLabel>
        <Select
          {...muiProps}
          id={id}
          value={selectValue}
          displayEmpty
          IconComponent={ExpandMoreIcon}
          renderValue={selected => getSelectRenderValue(selected)}
          onChange={e => handleSelectChange(e.target.value)}
          fullWidth={fullWidth}
          disabled={readOnly || muiProps.disabled}
          sx={{
            ...(readOnly && {
              '& .MuiSelect-select.Mui-disabled': {
                color: '#000000DE !important',
                WebkitTextFillColor: '#000000DE !important',
              },
            }),
          }}
        >
          {loading ? (
            <MenuItem disabled>
              <CommonLoader size={20} style={{ marginRight: 8 }} />
              Loading...
            </MenuItem>
          ) : !computedOptions.length ? (
            <MenuItem disabled>No Options Available</MenuItem>
          ) : (
            computedOptions.map((option, index) => {
              const isOptionObject = isObject(option)
              const displayText = isOptionObject
                ? isFunctionText(optionText)
                  ? optionText(option)
                  : option[optionText]
                : option

              return (
                <MenuItem
                  key={index}
                  title={displayText}
                  value={isOptionObject ? option[optionValue] : option}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding:
                      showCheckboxes || children ? '8px 9px' : '12px 9px',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: showCheckboxes ? '5px' : '0px',
                      }}
                    >
                      {showCheckboxes && multipleProp && (
                        <CommonCheckbox
                          value={isOptionChecked(option)}
                          style={{ marginRight: '12px' }}
                          stopNavigation={false}
                        />
                      )}
                      {displayText}
                    </div>
                    {children && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {typeof children === 'function'
                          ? children(option)
                          : children}
                      </div>
                    )}
                  </div>
                </MenuItem>
              )
            })
          )}
        </Select>
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 1,
              left: 0,
              right: 0,
              height: 3,
              margin: '0 2px',
            }}
          />
        )}
        {(error || helperText) && (
          <FormHelperText>{error || helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  )
}
